import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { KeyDetectionsPayload, KeyDetectionsVariables } from "../types";
import { CompanyFilter } from "../../../../../../../legacy/types/companyFilter";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../../utils/consts";
import { RECENT_INSTALLS_QUERY } from "../../../../../../../apis/companySearch/recentInstalls";
import { useWorkspaceStore } from "../../../../../../../store/workspace/WorkspaceStore";

interface Props {
  companyFilters: CompanyFilter;
  productIds: number[];
  localizeInstalls?: boolean;
}

export function useGetKeyDetections(props: Props) {
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const { data, loading } = useQuery<
    KeyDetectionsPayload,
    KeyDetectionsVariables
  >(RECENT_INSTALLS_QUERY, {
    variables: {
      companyFilters: props.companyFilters,
      productIds: props.productIds,
      localizeInstalls: props.localizeInstalls,
      companyMatchFile:
        props.companyFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
    },
    skip: props.productIds.length === 0,
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
  });

  //return top 5 detections for each product by revenue max
  const topKeyDetectionsData = useMemo(() => {
    if (!data?.recentInstalls || data.recentInstalls.length === 0) {
      return [];
    }

    const groupedByProduct: Record<number, typeof data.recentInstalls> = {};

    for (const install of data.recentInstalls) {
      if (!groupedByProduct[install.productId]) {
        groupedByProduct[install.productId] = [];
      }
      groupedByProduct[install.productId].push(install);
    }

    const topInstallsPerProduct = Object.values(groupedByProduct).map(
      (productInstalls) => {
        return productInstalls
          .sort(
            (productA, productB) => productB.revenueMax - productA.revenueMax
          )
          .slice(0, 5);
      }
    );

    return topInstallsPerProduct.flat();
  }, [data]);

  return {
    isLoading: loading,
    topKeyDetectionsData,
  };
}
